import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismButton,
  PrismButtonGroup,
  PrismCheckbox,
  PrismIcon,
  PrismNumberInput,
  PrismRadioGroup,
} from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import SettingsBudgetForecastModalBulkUpdateDateOrMonthRange from 'lumen/components/settings/budget-forecast/modal-bulk-update/date-or-month-range';
import type { BudgetForecastSegment } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  type DateGranularity,
  SEGMENT_LEVELS,
  type SegmentLevel,
} from 'lumen/utils/constants/budget-forecast-constants';
import { DAY_OF_WEEK_SHORT_TRANSLATIONS } from 'lumen/utils/constants/general-constants';

const METRICS = {
  ROOMS: 'rooms',
  ADR: 'adr',
} as const;
type Metric = (typeof METRICS)[keyof typeof METRICS];

type ChangeType = 'percentage' | 'absolute' | 'exact';

const SEGMENT_LEVEL_TO_DEPTH: Record<SegmentLevel, number> = {
  totals: -1,
  groups: 0,
  categories: 1,
  sub_categories: 2,
};

export interface CustomBulkEditOptions {
  changeType: ChangeType;
  changeValue: number | null;
  endDate: string;
  metric: Metric;
  segmentLevel: SegmentLevel;
  selectedSegmentIds: string[];
  selectedWeekdays: string[];
  startDate: string;
}

interface SettingsBudgetForecastModalBulkUpdateCustomBulkEditSignature {
  Args: {
    onOptionsChange: (options: Partial<CustomBulkEditOptions>) => void;
    options: CustomBulkEditOptions;
    segments: BudgetForecastSegment[];
    selectedDateRange: {
      endDate: string;
      granularity: DateGranularity;
      startDate: string;
    };
  };
}

export default class SettingsBudgetForecastModalBulkUpdateCustomBulkEdit extends Component<SettingsBudgetForecastModalBulkUpdateCustomBulkEditSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  get changeByValueLabel(): string | undefined {
    if (this.args.options.changeType === 'percentage') {
      return '%';
    }

    return this.exactValueLabel;
  }

  get exactValueLabel(): string | undefined {
    return this.args.options.metric === 'adr'
      ? this.hotelAndMarketService.hotel.currencySymbol
      : undefined;
  }

  get hasCategories(): boolean {
    return this.getSegmentsForLevel('categories').length > 0;
  }

  get hasGroups(): boolean {
    return this.getSegmentsForLevel('groups').length > 0;
  }

  get hasSubCategories(): boolean {
    return this.getSegmentsForLevel('sub_categories').length > 0;
  }

  get priceOption(): 'exact' | 'change_by' {
    return this.args.options.changeType === 'exact' ? 'exact' : 'change_by';
  }

  get segmentOptions(): { id: string; isSelected: boolean; label: string }[] {
    const { selectedSegmentIds } = this.args.options;

    return this.getSegmentsForLevel(this.args.options.segmentLevel).map(
      (segment) => ({
        id: segment.id!,
        label: segment.pathName,
        isSelected: selectedSegmentIds.includes(segment.id!),
      }),
    );
  }

  get selectedSegmentsErrorMessage(): string | undefined {
    if (this.args.options.segmentLevel === 'totals') {
      return undefined;
    }

    if (this.args.options.selectedSegmentIds.length !== 0) {
      return undefined;
    }

    return this.args.options.segmentLevel === 'groups'
      ? this.intl.t('budget_forecast.custom_bulk_edit.error.group')
      : this.intl.t('budget_forecast.custom_bulk_edit.error.category');
  }

  get weekdayOptions(): { id: string; label: string }[] {
    const startOfWeek = dayjs().startOf('week');
    return Array.from({ length: 7 }, (_, i) => {
      const date = startOfWeek.add(i, 'day');
      const isoWeekday = date.isoWeekday();

      return {
        id: `${isoWeekday}`,
        label: this.intl.t(
          DAY_OF_WEEK_SHORT_TRANSLATIONS[
            isoWeekday as keyof typeof DAY_OF_WEEK_SHORT_TRANSLATIONS
          ],
        ),
      };
    });
  }

  getSegmentsForLevel(level: SegmentLevel): BudgetForecastSegment[] {
    return this.args.segments.filter(
      (segment) => segment.depth === SEGMENT_LEVEL_TO_DEPTH[level],
    );
  }

  @action
  deselectAll(): void {
    this.args.onOptionsChange({ selectedSegmentIds: [] });
  }

  @action
  onChangeByTypeChange(value: string | string[]): void {
    if (Array.isArray(value)) {
      return;
    }

    if (value !== 'percentage' && value !== 'absolute') {
      return;
    }

    const changeValue =
      value === 'percentage' && this.args.options.changeValue !== null
        ? Math.max(this.args.options.changeValue, -100)
        : this.args.options.changeValue;

    this.args.onOptionsChange({ changeType: value, changeValue });
  }

  @action
  onChangeValueChange(changeValue: number | null): void {
    this.args.onOptionsChange({ changeValue });
  }

  @action
  onMetricChange(event: Event): void {
    const metric = (event.target as HTMLSelectElement).value as Metric;
    this.args.onOptionsChange({ metric });
  }

  @action
  onPriceOptionChange(event: Event): void {
    const value = (event.target as HTMLSelectElement)
      .value as typeof this.priceOption;

    const changeType = value === 'exact' ? 'exact' : 'percentage';
    this.args.onOptionsChange({ changeType, changeValue: null });
  }

  @action
  onRangeChange(startDate: string, endDate: string): void {
    this.args.onOptionsChange({ startDate, endDate });
  }

  @action
  onSegmentChange(segmentId: string): void {
    if (this.args.options.selectedSegmentIds.includes(segmentId)) {
      const selectedSegmentIds = this.args.options.selectedSegmentIds.filter(
        (id) => id !== segmentId,
      );
      this.args.onOptionsChange({ selectedSegmentIds });
    } else {
      const selectedSegmentIds = [
        ...this.args.options.selectedSegmentIds,
        segmentId,
      ];
      this.args.onOptionsChange({ selectedSegmentIds });
    }
  }

  @action
  onSegmentLevelChange(event: Event): void {
    const segmentLevel = (event.target as HTMLSelectElement)
      .value as SegmentLevel;

    const selectedSegmentIds = this.getSegmentsForLevel(segmentLevel).map(
      (segment) => segment.id!,
    );

    this.args.onOptionsChange({ segmentLevel, selectedSegmentIds });
  }

  @action
  onWeekdaysChange(selectedWeekdays: string | string[]): void {
    if (!Array.isArray(selectedWeekdays)) {
      selectedWeekdays = [selectedWeekdays];
    }

    this.args.onOptionsChange({ selectedWeekdays });
  }

  @action
  selectAll(): void {
    const selectedSegmentIds = this.segmentOptions.map((option) => option.id);
    this.args.onOptionsChange({ selectedSegmentIds });
  }

  <template>
    <div class="flex h-full flex-col gap-400">
      <div class="flex flex-col gap-200">
        <p class="text-300--medium">
          {{t "budget_forecast.custom_bulk_edit.select_metric"}}
        </p>

        <PrismRadioGroup
          @isHorizontal={{true}}
          @name="metric"
          @onChange={{this.onMetricChange}}
          @value={{@options.metric}}
          as |RadioGroup|
        >
          <RadioGroup.input @value={{METRICS.ROOMS}}>
            {{t "general.room_nights"}}
          </RadioGroup.input>

          <RadioGroup.input @value={{METRICS.ADR}}>
            {{t "settings.budget_forecast.adr.short"}}
          </RadioGroup.input>
        </PrismRadioGroup>
      </div>

      <div
        class="rounded-200 border-neutral-default bg-neutral-subdued flex flex-col gap-400 border border-solid p-400"
      >
        <SettingsBudgetForecastModalBulkUpdateDateOrMonthRange
          @endDate={{@options.endDate}}
          @granularity={{@selectedDateRange.granularity}}
          @maxDate={{@selectedDateRange.endDate}}
          @minDate={{@selectedDateRange.startDate}}
          @onRangeChange={{this.onRangeChange}}
          @startDate={{@options.startDate}}
        />

        {{#if (eq @selectedDateRange.granularity "day")}}
          <PrismButtonGroup
            @isSmall={{true}}
            @onChange={{this.onWeekdaysChange}}
            @value={{@options.selectedWeekdays}}
            class="w-full"
            as |ButtonGroup|
          >
            {{#each this.weekdayOptions as |weekdayButton|}}
              <ButtonGroup.button
                @label={{weekdayButton.label}}
                @value={{weekdayButton.id}}
                class="flex-1"
              />
            {{/each}}
          </PrismButtonGroup>

          {{#if (eq @options.selectedWeekdays.length 0)}}
            <div class="flex gap-100">
              <PrismIcon
                @color="critical"
                @name="circle-error-filled"
                @size="small"
              />
              <p class="text-200--medium text-critical-default">
                {{t "budget_forecast.custom_bulk_edit.error.weekdays"}}
              </p>
            </div>
          {{/if}}
        {{/if}}
      </div>

      <div class="border-neutral-default border-t border-solid"></div>

      <PrismRadioGroup
        @name="price-option"
        @onChange={{this.onPriceOptionChange}}
        @value={{this.priceOption}}
        as |RadioGroup|
      >
        <RadioGroup.input @value="change_by">
          <div class="flex flex-col gap-200">
            {{t "budget_forecast.custom_bulk_edit.change_by"}}

            {{#if (eq this.priceOption "change_by")}}
              <div class="flex gap-200">
                <div class="w-[104px]">
                  <PrismNumberInput
                    @inlineLabel={{this.changeByValueLabel}}
                    @locale={{this.hotelAndMarketService.user.locale}}
                    @min={{if (eq @options.changeType "percentage") -100}}
                    @onValueChange={{this.onChangeValueChange}}
                    @removePadding={{true}}
                    @required={{true}}
                    @requiredErrorMessage={{t
                      "budget_forecast.custom_bulk_edit.input_required"
                    }}
                    @step={{0.01}}
                    @value={{@options.changeValue}}
                  />
                </div>

                <PrismButtonGroup
                  @onChange={{this.onChangeByTypeChange}}
                  @value={{@options.changeType}}
                  as |ButtonGroup|
                >
                  <ButtonGroup.button @label="%" @value="percentage" />
                  <ButtonGroup.button @label="#" @value="absolute" />
                </PrismButtonGroup>
              </div>
            {{/if}}
          </div>
        </RadioGroup.input>

        <RadioGroup.input @value="exact">
          <div class="flex flex-col gap-200">
            {{t "budget_forecast.custom_bulk_edit.exact"}}

            {{#if (eq this.priceOption "exact")}}
              <div class="w-[104px]">
                <PrismNumberInput
                  @inlineLabel={{this.exactValueLabel}}
                  @locale={{this.hotelAndMarketService.user.locale}}
                  @min={{0}}
                  @onValueChange={{this.onChangeValueChange}}
                  @removePadding={{true}}
                  @required={{true}}
                  @requiredErrorMessage={{t
                    "budget_forecast.custom_bulk_edit.input_required"
                  }}
                  @step={{0.01}}
                  @value={{@options.changeValue}}
                />
              </div>
            {{/if}}
          </div>
        </RadioGroup.input>
      </PrismRadioGroup>

      <div class="border-neutral-default border-t border-solid"></div>

      <div class="flex flex-col gap-200">
        <p class="text-300--medium">
          {{t "budget_forecast.custom_bulk_edit.select_segments"}}
        </p>

        <PrismRadioGroup
          @isHorizontal={{true}}
          @name="segment-level"
          @onChange={{this.onSegmentLevelChange}}
          @value={{@options.segmentLevel}}
          as |RadioGroup|
        >
          <RadioGroup.input @value={{SEGMENT_LEVELS.TOTALS}}>
            {{t "budget_forecast.custom_bulk_edit.totals"}}
          </RadioGroup.input>
          {{#if this.hasGroups}}
            <RadioGroup.input @value={{SEGMENT_LEVELS.GROUPS}}>
              {{t "settings.central_mapping.hierarchy.groups"}}
            </RadioGroup.input>
          {{/if}}
          {{#if this.hasCategories}}
            <RadioGroup.input @value={{SEGMENT_LEVELS.CATEGORIES}}>
              {{t "settings.central_mapping.hierarchy.categories"}}
            </RadioGroup.input>
          {{/if}}
          {{#if this.hasSubCategories}}
            <RadioGroup.input @value={{SEGMENT_LEVELS.SUB_CATEGORIES}}>
              {{t "settings.central_mapping.hierarchy.sub_categories"}}
            </RadioGroup.input>
          {{/if}}
        </PrismRadioGroup>
      </div>

      <div
        class="rounded-200 border-neutral-default bg-neutral-subdued flex flex-col items-start gap-300 overflow-y-auto border border-solid p-400"
      >
        {{#if this.segmentOptions.length}}
          {{#let
            (eq this.segmentOptions.length @options.selectedSegmentIds.length)
            as |isAllSelected|
          }}
            <PrismButton
              @buttonType="ghost"
              @label={{if
                isAllSelected
                (t "budget_forecast.custom_bulk_edit.deselect_all")
                (t "budget_forecast.custom_bulk_edit.select_all")
              }}
              @onClick={{if isAllSelected this.deselectAll this.selectAll}}
              class="shrink-0"
            />
          {{/let}}
        {{/if}}

        <div class="ml-800 flex flex-col gap-400">
          {{#each this.segmentOptions as |segmentOption|}}
            <PrismCheckbox
              @checked={{segmentOption.isSelected}}
              @isSmall={{true}}
              @onChange={{fn this.onSegmentChange segmentOption.id}}
            >
              {{segmentOption.label}}
            </PrismCheckbox>
          {{else}}
            <PrismCheckbox
              @checked={{true}}
              @disabled={{true}}
              @isSmall={{true}}
            >
              {{t "budget_forecast.custom_bulk_edit.totals"}}
            </PrismCheckbox>
          {{/each}}
        </div>
      </div>

      {{#if this.selectedSegmentsErrorMessage}}
        <div class="flex gap-100">
          <PrismIcon
            @color="critical"
            @name="circle-error-filled"
            @size="small"
          />
          <p class="text-200--medium text-critical-default">
            {{this.selectedSegmentsErrorMessage}}
          </p>
        </div>
      {{/if}}
    </div>
  </template>
}
